import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  signInWithPopup,
  User,
} from 'firebase/auth';
import { getSuiteAuth, googleProvider } from '../config/firebase';
import { callEnsureUserDoc, callSyncManagedUserCredentials } from '../services/admin';

type AuthContextType = {
  user: User | null;
  loading: boolean;
  role: string | null;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<string | null>(null);

  useEffect(() => {
    const auth = getSuiteAuth();
    if (!auth) {
      setLoading(false);
      return;
    }
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const userData = await callEnsureUserDoc();
          setRole(userData.role || 'user');
        } catch {
          setRole('user');
        }
      } else {
        setRole(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const value = useMemo<AuthContextType>(() => ({
    user,
    loading,
    role,
    isAdmin: role === 'admin',
    async signIn(email: string, password: string) {
      const auth = getSuiteAuth();
      if (!auth) throw new Error('Auth not configured');
      await signInWithEmailAndPassword(auth, email, password);
    },
    async signUp(email: string, password: string) {
      const auth = getSuiteAuth();
      if (!auth) throw new Error('Auth not configured');
      await createUserWithEmailAndPassword(auth, email, password);
      await callSyncManagedUserCredentials({ password, productIds: ['vetaxis', 'staffaxis'] });
    },
    async signOut() {
      const auth = getSuiteAuth();
      if (!auth) return;
      await firebaseSignOut(auth);
    },
    async resetPassword(email: string) {
      const auth = getSuiteAuth();
      if (!auth) throw new Error('Auth not configured');
      await sendPasswordResetEmail(auth, email);
    },
    async signInWithGoogle() {
      const auth = getSuiteAuth();
      if (!auth) throw new Error('Auth not configured');
      await signInWithPopup(auth, googleProvider);
    },
  }), [user, loading, role]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
